<template>
  <!--
    One note about one part of the screen, written where that part is.

    It is a caption, not a callout: it sits in the flow above what it explains
    rather than floating over it. Nothing to position, nothing that can end up
    off-screen, nothing that covers the thing being described — and it reads in
    the same order as the page.
  -->
  <Transition name="guide">
    <aside v-if="guideShowing" class="guide" :style="{ '--guide-delay': `${Math.max(0, step - 1) * 70}ms` }">
      <span v-if="step" class="guide__step" aria-hidden="true">{{ step }}</span>
      <p class="guide__text"><slot>{{ text }}</slot></p>
    </aside>
  </Transition>
</template>

<script setup>
import { guideShowing } from '../composables/useGuide'

defineProps({
  // The reading order, shown as a small number. Explicit rather than counted
  // automatically: the order that matters is the one a person would follow, and
  // that is rarely the order the components happen to be declared in.
  step: { type: Number, default: 0 },
  // The text, for when a slot would be noise. Always translated by the caller:
  // the ui-kit ships no words of its own.
  text: { type: String, default: '' },
})
</script>

<style scoped>
.guide {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  margin-bottom: var(--space-2);
  font-size: var(--text-sm);
  line-height: 1.5;
  color: var(--color-text-soft);
}

.guide__step {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border-radius: var(--radius-full);
  /* Neutral, not the brand colour. --color-primary is whatever a client's brand
     happens to be — this instance's is a dark teal, which at 10% opacity behind
     itself made the number invisible on the dark theme. A border and muted text
     read the same in both themes and under any brand. */
  border: 1px solid var(--color-border);
  color: var(--color-text-mute);
  font-size: 10px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

/* Measured, not full width: a line of explanation nobody can follow across a
   1600px monitor has not explained anything. */
.guide__text { margin: 0; max-width: 72ch; }

/* They arrive in reading order, a beat apart, so the eye is led down the screen
   instead of being handed four paragraphs at once. Leaving is immediate and
   together: somebody who turned the guide off wants it gone, not choreographed. */
.guide-enter-active {
  transition: opacity var(--base) var(--ease), transform var(--base) var(--ease);
  transition-delay: var(--guide-delay, 0ms);
}
.guide-leave-active { transition: opacity var(--fast) var(--ease); }
.guide-enter-from { opacity: 0; transform: translateY(-4px); }
.guide-leave-to { opacity: 0; }

@media (prefers-reduced-motion: reduce) {
  .guide-enter-active { transition-delay: 0ms; }
}
</style>
